import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditSupervisor from "./EditSupervisor";
import "./ViewSupervisor.css";
import apiService from "../../utils/apiservice";

const ViewSupervisors = () => {
  const [rows, setRows] = useState([]);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [supervisorToDelete, setSupervisorToDelete] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const API_URL = process.env.REACT_APP_API_DJANGO_URL;
  const customerId = localStorage.getItem("customer_id");
  const token = localStorage.getItem("accessToken");

  const [deleteMessage, setDeleteMessage] = useState("");
  const [deleteError, setDeleteError] = useState(false);
  const [showReplaceDropdown, setShowReplaceDropdown] = useState(false);
  const [replacementSupervisor, setReplacementSupervisor] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Reset page when group or tab changes
  // ✅ Correct version for ViewSupervisors
  useEffect(() => {
    setPage(0);
  }, [rows.length]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // --- Fetch supervisors from API ---
  // const fetchSupervisors = async () => {
  //   if (!customerId || !token) return;
  //   try {
  //     const response = await axios.get(`${API_URL}/api/get_supervisors/${customerId}/`, {
  //       headers: {
  //         "Authorization": `Bearer ${token}`,
  //         "Accept": "application/json",
  //         "Content-Type": "application/json",
  //       },
  //       data: {},
  //     });

  //     const supervisors = response.data.supervisors.map((sup) => ({
  //       id: sup.id.id,             // Use the actual UUID from nested id
  //       first_name: sup.firstName,  // Use correct keys from API
  //       last_name: sup.lastName,
  //       email: sup.email,
  //       phone_no: sup.additionalInfo?.phone || sup.phone || "N/A", // fallback if null
  //       site_name: sup.site_name || "Not Assigned",
  //       user_id: sup.id.id,        // same as id, useful if needed for API calls
  //     }));
  //     setRows(supervisors);
  //   } catch (error) {
  //     console.error("Error fetching supervisors:", error);
  //     toast.error(error.response?.data?.error || "Failed to load supervisors");
  //   }
  // };

  const fetchSupervisors = async () => {
    if (!customerId || !token) return;

    try {
      // 🔹 1. Get supervisors
      // const res = await fetch(
      //   `${API_URL}/api/get_supervisors/${customerId}/`,
      //   {
      //     method: "GET",
      //     headers: {
      //       Authorization: `Bearer ${token}`,
      //       Accept: "application/json",
      //       "Content-Type": "application/json", // EXACT value
      //     },
      //   }
      // );
      const res = await apiService.get(`/api/get_supervisors/${customerId}/`, {
        data: {},
      });
      // if (!res.ok) {
      //   const errData = await res.json();
      //   throw new Error(errData.error || "Failed to fetch supervisors");
      // }

      // const data = await res.json();
      const data = res.data;
      const supervisorsData = data.supervisors;

      // 🔹 2. Fetch sites for each supervisor
      const supervisorsWithSites = await Promise.all(
        supervisorsData.map(async (sup) => {
          const userId = sup.id.id;

          try {
            const siteRes = await fetch(
              `${API_URL}/api/supervisor_sites/${userId}/`,
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${token}`,
                  Accept: "application/json",
                  "Content-Type": "application/json",
                },
              },
            );

            if (!siteRes.ok) {
              throw new Error("Failed to fetch sites");
            }

            const siteData = await siteRes.json();
            const sites = siteData.sites || [];

            const siteNames =
              sites.length > 0
                ? sites.map((s) => s.name).join(", ")
                : "Not Assigned";

            return {
              id: userId,
              first_name: sup.firstName,
              last_name: sup.lastName,
              email: sup.email,
              phone_no: sup.additionalInfo?.phone || sup.phone || "N/A",
              site_name: siteNames,
              user_id: userId,
            };
          } catch (err) {
            console.error(`Error fetching sites for ${userId}`, err);

            return {
              id: userId,
              first_name: sup.firstName,
              last_name: sup.lastName,
              email: sup.email,
              phone_no: sup.additionalInfo?.phone || sup.phone || "N/A",
              site_name: "Not Assigned",
              user_id: userId,
            };
          }
        }),
      );

      setRows(supervisorsWithSites);
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Something went wrong");
    }
  };

  useEffect(() => {
    fetchSupervisors();
  }, []);

  // --- Edit Logic ---
  const handleEditClick = (supervisor) => {
    setSelectedSupervisor(supervisor);
    setEditOpen(true);
  };

  const handleSaveEdit = (updatedSupervisor) => {
    setRows(
      rows.map((row) =>
        row.id === updatedSupervisor.id ? updatedSupervisor : row,
      ),
    );
    setEditOpen(false);
    setSelectedSupervisor(null);
  };
  // --- Delete Logic ---
  const handleDeleteClick = (supervisor) => {
    setSupervisorToDelete(supervisor);
    setDeleteOpen(true);
    setDeleteMessage("");
    setDeleteError(false);
  };

  const handleCancelDelete = () => {
    setDeleteOpen(false);
    setDeleteMessage("");
    setDeleteError(false);
  };

  const handleConfirmDelete = async () => {
    if (!supervisorToDelete) return;

    try {
      // const response = await axios({
      //   url: `${API_URL}/api/delete_supervisor/${supervisorToDelete.user_id}/`,
      //   method: "DELETE",
      //   headers: {
      //     Authorization: `Bearer ${token}`,
      //     Accept: "application/json",
      //     "Content-Type": "application/json",
      //   },
      //   data: {
      //     new_supervisor_id: replacementSupervisor || null, // ✅ send if selected
      //   },
      // });

      const response = await apiService.delete(
        `/api/delete_supervisor/${supervisorToDelete.user_id}/`,
        {
          data: {
            new_supervisor_id: replacementSupervisor || null,
          },
        },
      );
      const msg = response.data?.message || "Supervisor deleted successfully";

      setDeleteMessage(msg);
      setDeleteError(false);
      setShowReplaceDropdown(false);

      toast.success(msg);

      setTimeout(() => {
        setRows(rows.filter((r) => r.id !== supervisorToDelete.id));
        setDeleteOpen(false);
        setDeleteMessage("");
        setReplacementSupervisor("");
      }, 1500);
    } catch (error) {
      const backendMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to delete supervisor";

      setDeleteMessage(backendMessage);
      setDeleteError(true);

      toast.error(backendMessage);

      // 🔥 KEY LOGIC
      if (backendMessage.includes("new_supervisor_id")) {
        setShowReplaceDropdown(true);
      }
    }
  };

  return (
    <Box className="view-supervisors-container">
      <TableContainer component={Paper} className="supervisor-table-wrapper">
        <Table stickyHeader>
          <TableHead >
            <TableRow >
              <TableCell align="center" className="table-header">Sr No</TableCell>
              <TableCell align="center" className="table-header">First Name</TableCell>
              <TableCell align="center" className="table-header">Last Name</TableCell>
              <TableCell align="center" className="table-header">Email</TableCell>
              <TableCell  align="center" className="table-header">Phone Number</TableCell>
              <TableCell align="center" className="table-header">Assigned Site</TableCell>
              <TableCell align="center" className="table-header">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ py: 5, fontWeight: 500 }}
                >
                  No Supervisors Are Available
                </TableCell>
              </TableRow>
            ) : (
              rows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow key={row.id} hover>
                    <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell align="center">{row.first_name}</TableCell>
                    <TableCell align="center">{row.last_name}</TableCell>
                    <TableCell align="center">{row.email}</TableCell>
                    <TableCell align="center">{row.phone_no}</TableCell>
                    <TableCell align="center">
                      <Box className="site-badge">{row.site_name}</Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => handleEditClick(row)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(row)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>

        {/* After </TableContainer> */}
        {rows.length > 0 && (
          <div className="pagination-bar">
            <div className="rows-select">
              Rows per page:
              <select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
              </select>
            </div>
            <div className="page-info">
              {page * rowsPerPage + 1}-
              {Math.min((page + 1) * rowsPerPage, rows.length)} of {rows.length}
            </div>
            <div className="page-arrows">
              <button disabled={page === 0} onClick={() => setPage(page - 1)}>
                ‹
              </button>
              <button
                disabled={(page + 1) * rowsPerPage >= rows.length}
                onClick={() => setPage(page + 1)}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </TableContainer>

      <EditSupervisor
        open={editOpen}
        onClose={() => setEditOpen(false)}
        supervisor={selectedSupervisor}
        onSave={handleSaveEdit}
      />

      <Dialog open={deleteOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Supervisor</DialogTitle>

        <DialogContent>
          {/* Default Confirmation */}
          {!deleteMessage && (
            <DialogContentText>
              Are you sure you want to delete{" "}
              <b>{supervisorToDelete?.first_name}</b>?
            </DialogContentText>
          )}

          {/* ✅ Backend Message Box */}
          {deleteMessage && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: deleteError ? "#fdecea" : "#e8f5e9",
                color: deleteError ? "#d32f2f" : "#2e7d32",
                fontWeight: 500,
              }}
            >
              {deleteMessage}
            </Box>
          )}

          {/* ✅ 🔥 SHOW DROPDOWN IF REQUIRED */}
          {showReplaceDropdown && (
            <Box sx={{ mt: 3 }}>
              <Typography sx={{ mb: 1, fontWeight: 500 }}>
                Select Replacement Supervisor
              </Typography>

              <select
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
                value={replacementSupervisor}
                onChange={(e) => setReplacementSupervisor(e.target.value)}
              >
                <option value="">Select Supervisor</option>

                {rows
                  .filter((r) => r.id !== supervisorToDelete?.id)
                  .map((sup) => (
                    <option key={sup.id} value={sup.user_id}>
                      {sup.first_name} {sup.last_name}
                    </option>
                  ))}
              </select>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {/* ❌ If error + dropdown → show Cancel + Delete */}
          {/* {!deleteError && !showReplaceDropdown && ( */}
          {!deleteError && !showReplaceDropdown && !deleteMessage && (
            <>
              <Button onClick={handleCancelDelete} variant="outlined">
                Cancel
              </Button>

              <Button
                onClick={handleConfirmDelete}
                variant="contained"
                color="error"
              >
                Delete
              </Button>
            </>
          )}

          {/* 🔥 If dropdown is shown */}
          {showReplaceDropdown && (
            <>
              <Button onClick={handleCancelDelete} variant="outlined">
                Cancel
              </Button>

              <Button
                onClick={handleConfirmDelete}
                variant="contained"
                color="error"
                disabled={!replacementSupervisor} // ✅ must select
              >
                Delete with Replacement
              </Button>
            </>
          )}

          {/* ❌ Only error (no dropdown) → OK button */}
          {deleteError && !showReplaceDropdown && (
            <Button onClick={handleCancelDelete} variant="contained">
              OK
            </Button>
          )}

          {/* ✅ Success state */}
          {deleteMessage && !deleteError && !showReplaceDropdown && (
            <Button
              onClick={handleCancelDelete}
              variant="contained"
              color="success"
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
    </Box>
  );
};

export default ViewSupervisors;
